/**
 * App Component
 *
 * Root component for the Vibe Designer SPA.
 *
 * Workstream A.10 (2026-04-26): vibe-designer is a Mode 1 (OIDC) app. The SPA owns
 * its own login flow via Authentik OIDC + PKCE; AuthProvider wraps Routes and
 * ProtectedRoute gates the workspace. Plan D D.G1/D.G2 had wrongly forced Mode 2
 * (Traefik ForwardAuth at the route layer), which broke fresh OIDC login because
 * the SPA's PKCE flow targets `/auth/callback` while Mode 2 expected the outpost
 * callback at `/outpost.goauthentik.io/callback`.
 *
 * Plan E features (TerminalsShell + useTenantStore.fetchTenant + Designer/Terminals
 * view switcher) are preserved verbatim inside <VibeDesignerApp/> below — auth wall
 * was added back ON TOP of HEAD, never via a pre-D.G2 checkout.
 */
import { useEffect, useMemo } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AppShell } from './components/layout/AppShell';
import { TopBar } from './components/layout/TopBar';
import { TriShellLayout } from './components/layout/TriShellLayout';
import { ViewportGuard } from './components/layout/ViewportGuard';
import { MindChatWidget } from '@/components/chat/MindChatWidget';
import { LiveAgentsDock } from './components/layout/LiveAgentsDock';
import './components/layout/LiveAgentsDock.css';
import { VersionFooter } from './components/layout/VersionFooter';
import { TerminalsShell } from './components/terminals/TerminalsShell';
import { DashboardShell } from './components/dashboard/DashboardShell';
import { KnowledgeShell } from './components/knowledge/KnowledgeShell';
import { ModuleHostPane } from './components/modules/ModuleHostPane';
import { DeploysShell } from './components/deploys/DeploysShell';
import { PiChatDock } from './components/layout/PiChatDock';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { ToastProvider } from './components/Toast';
import { useProjectStore } from './stores/projectStore';
import { useTenantStore } from './stores/tenantStore';
import { useConfigStore } from './stores/configStore';
import { useUIStore } from './stores/uiStore';
import { useKeyboardShortcuts, createDefaultShortcuts } from './hooks/useKeyboardShortcuts';
import { useAutoRebuildOnSave } from './hooks/useAutoRebuildOnSave';
import { useVisualViewport } from './hooks/useVisualViewport';
// 1.421.0 — tenant-unique document.title ("<tenant> — Vibe Designer") so
// bookmarks across tenants are distinguishable.
import { useTenantPageTitle } from './hooks/useTenantPageTitle';

/**
 * Main app content — the Vibe Designer workspace.
 * Plan D: rendered directly. Traefik ForwardAuth handles auth at the route layer.
 */
function VibeDesignerApp() {
  const ensureProject = useProjectStore((s) => s.ensureProject);
  const expandFolder = useProjectStore((s) => s.expandFolder);
  const fileTreeRootId = useProjectStore((s) => s.fileTree?.id);
  const fetchTenant = useTenantStore((s) => s.fetchTenant);
  const fetchConfig = useConfigStore((s) => s.fetchConfig);
  const viewMode = useUIStore((s) => s.viewMode);
  const setViewMode = useUIStore((s) => s.setViewMode);
  const interfaceFontSize = useUIStore((s) => s.interfaceFontSize);
  // "Pi chat on all tabs" P1 — the Pi chat dock is DESKTOP-only (touch keeps
  // its own Chat pane in MobileStackedShell; a right-drawer is wrong on a
  // phone). isTouch is set at mount by AppShell's useIsTouch(); default false.
  const isTouch = useUIStore((s) => s.isTouch);
  // 1.421.0 — retitle the tab once /api/me resolves the tenant.
  useTenantPageTitle();

  // Global agent CONTROL: switch the top-level view on command via the
  // 'vibe:module-action' bus (Pi's ui_action tool). Lives here at the
  // App/chrome level (NOT per-shell) because switching the active view is
  // sibling-chrome, above any single shell. Reuses the SAME verb name voice
  // already uses (`switch_view`, applyVoiceSpaAction in voiceStore) so there's
  // one vocabulary across voice + Pi — voice handles it directly (no bus), Pi
  // routes it through the bus to here. Benign: just changes which always-
  // mounted shell is visible. Per-surface listeners (set_tab / select_workflow
  // / set_nav) handle in-view control.
  useEffect(() => {
    const onModuleAction = (e: Event) => {
      const d = (e as CustomEvent).detail as
        | { action?: string; params?: Record<string, unknown> }
        | undefined;
      if (d?.action !== 'switch_view') return;
      const v = d.params?.view;
      const valid = ['dashboard', 'designer', 'terminals', 'knowledge', 'modules', 'deploys'];
      if (typeof v === 'string' && valid.includes(v)) {
        setViewMode(v as typeof viewMode);
      }
    };
    window.addEventListener('vibe:module-action', onModuleAction);
    return () => window.removeEventListener('vibe:module-action', onModuleAction);
  }, [setViewMode]);

  // 1.88.0: apply global UI font size to the document root. Tailwind's text-*
  // classes are rem-based (text-xs=0.75rem, text-sm=0.875rem, text-base=1rem,
  // ...) so changing the html element's font-size scales every Tailwind text
  // class proportionally. Runs on mount + every change. Pi Chat panel keeps
  // its own per-tenant override via --vibe-pi-chat-font-size CSS variable
  // (chatPrefsStore.fontSize). Monaco editor has its own fontSize option
  // independent of HTML font-size and is NOT affected by this hook.
  useEffect(() => {
    document.documentElement.style.fontSize = `${interfaceFontSize}px`;
    return () => {
      // Reset on unmount so embedding contexts aren't left with our value.
      // No-op in production where the SPA owns the document.
      document.documentElement.style.removeProperty('font-size');
    };
  }, [interfaceFontSize]);

  // Phase B (E.21.37): subscribe to vibe:file:save:* events for auto-
  // rebuild + toast progression. Hook reads useConfigStore.autoRebuildOnSave
  // at event time, so no mount-time race; ToastProvider is mounted at the
  // App() root (below) which wraps every render of VibeDesignerApp.
  useAutoRebuildOnSave();

  // 1.124.0 Phase 2a: listen to window.visualViewport so the Pi chat
  // queue row's `--keyboard-offset` CSS var can track the iOS Safari
  // soft keyboard. No-op on desktop (no visualViewport resize from
  // keyboard) and on older browsers (graceful feature-detect fallback).
  useVisualViewport();

  // E.21.28: load real /workspace project on mount (replaces demoFileTree).
  // ensureProject() lists projects, auto-creates a default if none exist,
  // sets currentProject + fileTree from the live FileNode response.
  useEffect(() => {
    void ensureProject();
  }, [ensureProject]);

  // E.21.31.1 (2026-05-04): auto-expand the file tree's actual root node
  // once it loads. Pre-fix this useEffect set `new Set(['root', 'src'])`
  // — hardcoded IDs from the long-deleted demoFileTree which never
  // matched the real backend's UUID-derived node IDs (uuid.uuid5 over
  // each path). Result: the Explorer panel rendered with the root
  // collapsed and folder clicks did nothing (separately broken by the
  // missing enableMapSet — both fixed in E.21.31.1).
  useEffect(() => {
    if (fileTreeRootId) {
      expandFolder(fileTreeRootId);
    }
  }, [fileTreeRootId, expandFolder]);

  // Plan E E.5+E.6: fetch tenant identity once on mount; all terminal URL
  // construction reads from tenantStore (atomic guarantee for E.6).
  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  // Phase B (E.21.37): fetch SPA-facing config from /api/config so
  // useAutoRebuildOnSave can gate on autoRebuildOnSave. Idempotency guard
  // in configStore matches tenantStore so React StrictMode double-invoke
  // is benign.
  useEffect(() => {
    void fetchConfig();
  }, [fetchConfig]);

  // Briefing Room M1 (2026-05-31) — Signal A of the two-signal host-detection
  // contract (apps/briefing-room-module/schema/host_probe.md). Module SPAs
  // probe `window.__vibeDesignerHost` at boot to detect they are inside
  // vibe-designer rather than running as a standalone HTML bundle.
  //
  // The `capabilities` array is left empty here — modules use Signal B
  // (GET /api/host-capabilities) to enumerate live capabilities. Presence
  // of the probe object alone confirms host-mode; capability content comes
  // from the backend probe.
  //
  // `version` is the host's reported version. v1 hardcodes the M-track
  // scaffold version; future work can swap to import.meta.env.VITE_APP_VERSION
  // once the build pipeline wires the env var through.
  useEffect(() => {
    (window as unknown as { __vibeDesignerHost?: { version: string; capabilities: string[] } }).__vibeDesignerHost = {
      version: '0.1.0',
      capabilities: [],
    };
    return () => {
      delete (window as unknown as { __vibeDesignerHost?: unknown }).__vibeDesignerHost;
    };
  }, []);

  // Phase A: register Cmd+S / Ctrl+S as the SPA-wide save shortcut. Handler
  // reads activeFileId via getState() at call time so the closure never
  // captures a stale id (avoids the typical React stale-closure pitfall
  // when the user switches tabs without remounting App).
  const shortcuts = useMemo(
    () =>
      createDefaultShortcuts({
        saveFile: () => {
          const { activeFileId, saveFile } = useProjectStore.getState();
          if (activeFileId) void saveFile(activeFileId);
        },
      }),
    [],
  );
  useKeyboardShortcuts(shortcuts);

  return (
    <ViewportGuard>
      {/* E.21.24 + 1.168.0 Phase 1: keep ALL 3 shells mounted; toggle via
          display:contents/none. Pre-fix (E.21.24) ternary fully unmounted
          the inactive shell on every tab toggle, destroying xterm state +
          WS connections + ttyd PTY sessions + voice tabs' per-tab state.
          1.168.0 extends DualShellLayout → TriShellLayout to add Knowledge.

          1.207.3: hoisted Footer-aware flex column above TriShellLayout so
          the VersionFooter sits as a flex sibling (NOT fixed-positioned).
          Pre-fix the fixed bottom-0 z-30 Footer overlapped bottom-anchored
          content (Pi chat input + voice tabs list). Shells now use h-full
          (parent flex-1 carves out the footer height). MindChatWidget
          remains outside the flex column — since 1.555.0 it renders only the
          sliding panel (fixed inset-0 when open), opened from the TopBar 🧠
          button; the old fixed bottom-right FAB was removed. */}
      <div className="flex flex-col h-screen w-screen overflow-hidden bg-vibe-bg text-vibe-text">
        {/* "Hoist TopBar to App level" (2026-07-03): the top nav is rendered
            ONCE here, outside TriShellLayout's display:contents/none toggle, so
            it spans the FULL width ABOVE the [shells | PiChatDock] row and is
            present on every view by construction (holds the 1.170.1 invariant
            at the App level). Per operator, ONLY TopBar hoists — the voice
            strip STAYS per-shell so it keeps shrinking beside the dock. */}
        <TopBar />
        {/* "Pi chat on all tabs" P1 (2026-07-03): the content region is a flex
            ROW — the shells fill the left (flex-1) and the PiChatDock is an
            INLINE right column (a flex sibling, NOT an overlay), so the shell
            content reflows to leave room for it instead of being occluded.
            One reservation here reflows all five views uniformly. */}
        <div className="flex-1 min-h-0 overflow-hidden flex">
          <div className="relative min-w-0 flex-1">
            <TriShellLayout
              viewMode={viewMode}
              dashboard={<DashboardShell />}
              designer={<AppShell />}
              knowledge={<KnowledgeShell />}
              terminals={<TerminalsShell />}
              modules={<ModuleHostPane />}
              deploys={<DeploysShell />}
            />
          </div>
          {/* The ONE hoisted Pi/legacy chat instance — present on every view,
              never per-shell replicated (F1 stream-loss). Desktop-only. */}
          {!isTouch && <PiChatDock />}
        </div>
        <VersionFooter />
      </div>
      <MindChatWidget />
      {/* GM Attention Phase 1 (2026-07-17) — App-level overlay (both desktop +
          touch, like MindChatWidget): live agent roster + remote approvals. */}
      <LiveAgentsDock />
    </ViewportGuard>
  );
}

function App() {
  // Workstream A.10: AuthProvider wraps Routes so /login + /auth/callback can call
  // useAuth(); ProtectedRoute gates the workspace. /auth/callback handler lives in
  // AuthContext.tsx — it exchanges the OIDC code for tokens and redirects to "/".
  //
  // Phase B (E.21.37) Reviewer 1 Critical 1: ToastProvider must wrap
  // VibeDesignerApp because useAutoRebuildOnSave (mounted inside) calls
  // useToast(), which throws if no ToastProvider is in the React tree.
  // Toasts on the LoginPage path are not currently used; if ever needed
  // ToastProvider can move outside Routes.
  return (
    <AuthProvider>
      <ToastProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<LoginPage />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <VibeDesignerApp />
              </ProtectedRoute>
            }
          />
        </Routes>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
